import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { SUPERUSER_BYPASS_EMAIL, SUPERUSER_BYPASS_ENABLED } from "@/hooks/useAuth";

type Options = {
  intervalMs?: number; // how often to update location
  enableHighAccuracy?: boolean;
  // timeout for acquiring location (ms)
  timeoutMs?: number;
  // initial privacy level: 'off' | 'coarse' | 'precise'
  initialPrivacyLevel?: PrivacyLevel;
};

export type PrivacyLevel = "off" | "coarse" | "precise";

/**
 * useLocationSharing
 * - periodically reads browser geolocation and upserts the current user's lat/lng to the `users` table
 * - returns controls to start/stop sharing and the current status
 *
 * Usage:
 * const { start, stop, sharing, error } = useLocationSharing({ intervalMs: 15000 })
 * call start(currentUserId) after sign-in. Call stop() on sign-out or when app goes background.
 */
export default function useLocationSharing(opts?: Options) {
  const intervalMs = opts?.intervalMs ?? 15000;
  const enableHighAccuracy = opts?.enableHighAccuracy ?? true;
  const timeoutMs = opts?.timeoutMs ?? 90000;
  const GRACE_MS = 120000; // suppress errors for 2 minutes after a success

  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // watchId can be a string (Capacitor) or number (browser) depending on platform
  const watchIdRef = useRef<string | number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const lastSuccessRef = useRef<number | null>(null);

  const BYPASS_SHARING_KEY = "giftmap_bypass_sharing";

  const isBypassUserId = (userId: string | null | undefined) => {
    if (!SUPERUSER_BYPASS_ENABLED || !userId) return false;
    try {
      const bypassEnabled = localStorage.getItem("giftmap_superuser_bypass") === "true";
      const email = localStorage.getItem("giftmap_superuser_bypass_email") || SUPERUSER_BYPASS_EMAIL;
      return bypassEnabled && email.toLowerCase() === SUPERUSER_BYPASS_EMAIL && userId === "00000000-0000-0000-0000-000000000001";
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!SUPERUSER_BYPASS_ENABLED) return;
    try {
      const bypassEnabled = localStorage.getItem("giftmap_superuser_bypass") === "true";
      if (!bypassEnabled) return;
      const v = localStorage.getItem(BYPASS_SHARING_KEY);
      if (v === "true") {
        setSharing(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const stop = useCallback(() => {
    setSharing(false);
    setError(null);
    // Clear native or browser watchers
    try {
      const isNative = typeof Capacitor !== "undefined" && Capacitor.getPlatform && Capacitor.getPlatform() !== "web";
      if (watchIdRef.current != null) {
        if (isNative) {
          try {
            // Capacitor Geolocation.clearWatch expects an object with id
            (async () => {
              try {
                await Geolocation.clearWatch({ id: String(watchIdRef.current) });
              } catch {}
            })();
          } catch {}
        } else if (navigator.geolocation && (navigator.geolocation as any).clearWatch) {
          try {
            navigator.geolocation.clearWatch(watchIdRef.current as number);
          } catch {}
        }
        watchIdRef.current = null;
      }

      if (intervalRef.current != null) {
        try {
          window.clearInterval(intervalRef.current);
        } catch {}
        intervalRef.current = null;
      }
    } catch (e) {
      // swallow
    }

    // Mark user as not active and clear stored location on stop
    const uid = currentUserIdRef.current;
    if (uid && !isBypassUserId(uid)) {
      (async () => {
        try {
          await supabase.from("users").upsert({ id: uid, is_active: false, raw_lat: null, raw_lng: null });
        } catch {}
      })();
    }

    if (uid && isBypassUserId(uid)) {
      try {
        localStorage.setItem(BYPASS_SHARING_KEY, "false");
      } catch {}
    }

    currentUserIdRef.current = null;
    lastSuccessRef.current = null;
  }, []);

  const [privacyLevel, setPrivacyLevelState] = useState<PrivacyLevel>(opts?.initialPrivacyLevel ?? (localStorage.getItem("giftmap_privacy_level") as PrivacyLevel) ?? "precise");

  const upsertLocation = useCallback(async (userId: string, lat: number | null, lng: number | null, level: PrivacyLevel = "precise") => {
    if (isBypassUserId(userId)) {
      setError(null);
      return;
    }

    try {
      // Client should send raw coordinates and privacy_level; server trigger enforces public lat/lng
      // Explicitly set location_updated_at to ensure it's always updated (don't rely solely on trigger)
      const payload: any = {
        id: userId,
        raw_lat: lat,
        raw_lng: lng,
        privacy_level: level,
        is_active: level !== "off" && lat != null && lng != null,
        location_updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("users").upsert(payload, { onConflict: "id" });
      if (error) {
        console.error("Failed upsert location:", error);
        setError(error.message);
      }
    } catch (e: any) {
      console.error("Unexpected error upserting location:", e);
      setError(e?.message ?? String(e));
    }
  }, []);

  const checkInOnce = useCallback(
    async (userId: string, level: PrivacyLevel = privacyLevel) => {
      if (!userId) {
        setError("No user id provided");
        return { ok: false, error: "no_user" } as const;
      }

      try {
        // On native, request permissions and get one reading
        const isNative = typeof Capacitor !== "undefined" && Capacitor.getPlatform && Capacitor.getPlatform() !== "web";
      if (isNative) {
        try {
          const perm = await Geolocation.requestPermissions();
          const status = (perm as any)?.location || (perm as any)?.coarseLocation;
          // Only treat explicit "denied" as a failure
          // "prompt" means permission dialog will appear on getCurrentPosition
          if (status === "denied") {
            setError("Location permission denied");
            await upsertLocation(userId, null, null, "off");
            return { ok: false, error: "permission_denied" } as const;
          }
        } catch (e: any) {
          // On iOS, requestPermissions can throw if location services are disabled
          // Don't treat this as a hard failure - try getCurrentPosition anyway
          console.warn("requestPermissions error (will try getCurrentPosition anyway):", e?.message);
        }

          try {
            const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout: timeoutMs });
            if (pos?.coords) {
              await upsertLocation(userId, pos.coords.latitude, pos.coords.longitude, level);
              lastSuccessRef.current = Date.now();
              setError(null);
              return { ok: true } as const;
            }
            setError("No position available");
            await upsertLocation(userId, null, null, level);
            return { ok: false, error: "no_position" } as const;
          } catch (e: any) {
            // Fallback to low accuracy on timeout
            const msg = (e?.message ?? String(e)).toLowerCase();
            if (enableHighAccuracy && (msg.includes("time") || msg.includes("timeout"))) {
              try {
                const pos2 = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: timeoutMs });
                if (pos2?.coords) {
                  await upsertLocation(userId, pos2.coords.latitude, pos2.coords.longitude, level);
                  lastSuccessRef.current = Date.now();
                  setError(null);
                  return { ok: true } as const;
                }
              } catch (e2: any) {
                setError(e2?.message ?? String(e2));
                await upsertLocation(userId, null, null, level);
                return { ok: false, error: "timeout" } as const;
              }
            }
            setError(e?.message ?? String(e));
            await upsertLocation(userId, null, null, level);
            return { ok: false, error: "error" } as const;
          }
        }

        // Web: just read once via browser geolocation
        return await new Promise<{ ok: true } | { ok: false; error: string }>((resolve) => {
          if (!navigator.geolocation) {
            setError("Geolocation is not available in this browser");
            upsertLocation(userId, null, null, level).then(() => resolve({ ok: false, error: "unavailable" }));
            return;
          }
          const tryWebCurrent = (highAccuracy: boolean) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                upsertLocation(userId, pos.coords.latitude, pos.coords.longitude, level).then(() => {
                  lastSuccessRef.current = Date.now();
                  setError(null);
                  resolve({ ok: true });
                });
              },
              (err) => {
                const code = (err as any)?.code;
                const msg = err?.message || "Geolocation error";
                if (highAccuracy && (code === 3 || msg.toLowerCase().includes("time"))) {
                  tryWebCurrent(false);
                  return;
                }
                setError(msg);
                upsertLocation(userId, null, null, level).then(() => resolve({ ok: false, error: "error" }));
              },
              { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 0 }
            );
          };
          tryWebCurrent(enableHighAccuracy);
        });
      } catch (e: any) {
        setError(e?.message ?? String(e));
        try { await upsertLocation(userId, null, null, level); } catch {}
        return { ok: false, error: "error" } as const;
      }
    },
    [enableHighAccuracy, timeoutMs, upsertLocation, privacyLevel]
  );

  const start = useCallback(
    (userId: string, level: PrivacyLevel = privacyLevel) => {
      if (!userId) {
        setError("No user id provided");
        return;
      }

      if (isBypassUserId(userId)) {
        try {
          localStorage.setItem(BYPASS_SHARING_KEY, "true");
        } catch {}
      }

      // stop any existing
      stop();
      currentUserIdRef.current = userId;

      const isNative = typeof Capacitor !== "undefined" && Capacitor.getPlatform && Capacitor.getPlatform() !== "web";

      setSharing(true);
      setError(null);

      // Native (Capacitor) implementation using @capacitor/geolocation when available
      if (isNative) {
        (async () => {
          try {
            // Ensure runtime permission is granted on device before reading location
            try {
              await Geolocation.requestPermissions();
            } catch {}

            // Try to get current position with high accuracy first; if it times out, fallback to low accuracy
            const tryGetCurrent = async () => {
              try {
                const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout: timeoutMs });
                if (currentUserIdRef.current && pos?.coords) {
                  upsertLocation(currentUserIdRef.current, pos.coords.latitude, pos.coords.longitude, level);
                  lastSuccessRef.current = Date.now();
                  setError(null);
                }
              } catch (e: any) {
                const msg = (e?.message ?? String(e)).toLowerCase();
                // Fallback with lower accuracy if timeout or similar
                if (enableHighAccuracy && (msg.includes("time") || msg.includes("timeout"))) {
                  try {
                    const pos2 = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: timeoutMs });
                    if (currentUserIdRef.current && pos2?.coords) {
                      upsertLocation(currentUserIdRef.current, pos2.coords.latitude, pos2.coords.longitude, level);
                      lastSuccessRef.current = Date.now();
                      setError(null);
                      return;
                    }
                  } catch (e2: any) {
                    console.error("Capacitor geolocation fallback error:", e2);
                    setError(e2?.message ?? String(e2));
                    return;
                  }
                }
                console.error("Capacitor geolocation getCurrentPosition error:", e);
                const now = Date.now();
                if (lastSuccessRef.current && now - lastSuccessRef.current < GRACE_MS) {
                  // suppress transient error shortly after a success
                  return;
                }
                setError(e?.message ?? String(e));
              }
            };

            await tryGetCurrent();
          } catch (e: any) {
            console.error("Capacitor geolocation getCurrentPosition error:", e);
            setError(e?.message ?? String(e));
          }

          try {
            const id = await Geolocation.watchPosition(
              { enableHighAccuracy, timeout: timeoutMs },
              (position, err) => {
                if (err) {
                  console.error("Capacitor watchPosition error:", err);
                  const msg = ((err as any)?.message || "Geolocation watch error");
                  const now = Date.now();
                  if (lastSuccessRef.current && now - lastSuccessRef.current < GRACE_MS) {
                    // suppress transient errors if we have a recent success
                    return;
                  }
                  setError(msg);
                  return;
                }
                if (!position || !position.coords) return;
                if (currentUserIdRef.current) {
                  upsertLocation(currentUserIdRef.current, position.coords.latitude, position.coords.longitude, level);
                  lastSuccessRef.current = Date.now();
                  setError(null);
                }
              }
            );
            // watchPosition returns a string id on Capacitor
            watchIdRef.current = id as unknown as string;
          } catch (e: any) {
            console.error("Failed to start Capacitor watchPosition:", e);
          }
        })();
      } else {
        // Browser fallback
        if (!navigator.geolocation) {
          setError("Geolocation is not available in this browser");
          setSharing(false);
          return;
        }

        // Fire one immediate location read then schedule periodic reads via setInterval.
        const readAndSend = () => {
          const tryWebCurrent = (highAccuracy: boolean) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                if (currentUserIdRef.current) upsertLocation(currentUserIdRef.current, lat, lng, level);
                lastSuccessRef.current = Date.now();
                setError(null);
              },
              (err) => {
                console.error("Geolocation error:", err);
                const code = (err as any)?.code;
                const msg = err?.message || "Geolocation error";
                // If timeout in web, fallback to low accuracy once
                if (highAccuracy && (code === 3 || msg.toLowerCase().includes("time"))) {
                  tryWebCurrent(false);
                  return;
                }
                const now = Date.now();
                if (lastSuccessRef.current && now - lastSuccessRef.current < GRACE_MS) {
                  return;
                }
                setError(msg);
              },
              { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 0 }
            );
          };
          tryWebCurrent(enableHighAccuracy);
        };

        // initial
        readAndSend();

        // schedule repeating updates
        const id = window.setInterval(() => {
          readAndSend();
        }, intervalMs) as unknown as number;

        intervalRef.current = id;
      }
    },
    [intervalMs, enableHighAccuracy, stop, upsertLocation]
  );

  // Note: Cleanup is managed by LocationSharingContext, not on component unmount
  // This allows location sharing to persist across page navigation

  // setter that persists preference
  const setPrivacyLevel = (level: PrivacyLevel) => {
    try {
      localStorage.setItem("giftmap_privacy_level", level);
    } catch {}
    setPrivacyLevelState(level);
    // if currently sharing, and turning off, immediately clear location in DB
    if (currentUserIdRef.current) {
      if (level === "off") {
        upsertLocation(currentUserIdRef.current, null, null, level).catch(() => {});
      }
    }
  };

  return { start, stop, sharing, error, privacyLevel, setPrivacyLevel, checkInOnce } as const;
}
